import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { TrendingUp, TrendingDown, ArrowRight, Calendar, Brain, Activity, Share2, Download, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api, DOMAIN_NAMES, DOMAIN_SHORT_NAMES, formatDate, getScoreLevel } from '../lib/api';
import { toast } from 'sonner';

export default function Progress() {
  const [snapshots, setSnapshots] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [shareCard, setShareCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const [snapshotsRes, comparisonRes] = await Promise.all([
          api.get('/progress'),
          api.get('/progress/comparison')
        ]);
        setSnapshots(snapshotsRes.data.snapshots || []);
        setComparison(comparisonRes.data);
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, []);

  const fetchShareCard = async () => {
    try {
      const response = await api.get('/progress/share-card');
      setShareCard(response.data);
    } catch (error) {
      toast.error('Unable to generate share card');
    }
  };

  const copyShareText = () => {
    if (shareCard?.share_text) {
      navigator.clipboard.writeText(shareCard.share_text + '\n\nPowered by MindFlō - Human Mental Operating System');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading progress...</p>
        </div>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <TrendingUp className="h-16 w-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
        <h2 className="text-xl font-semibold mb-2">No Progress Data Yet</h2>
        <p className="text-muted-foreground mb-6">Complete assessments over time to track your cognitive evolution.</p>
        <Link to="/assessment/full">
          <Button className="glow-primary" data-testid="start-assessment">
            Start Diagnostic
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  // Prepare chart data with new terminology
  const chartData = snapshots.map((s, index) => ({
    name: `Day ${s.days_since_baseline || index * 30}`,
    stabilityIndex: s.stability_index || s.mind_index || 50,
    neuralFriction: s.neural_friction_score ?? s.cognitive_risk_index ?? 0,
    beliefFlexibility: s.belief_flexibility_score || 50,
    ...Object.fromEntries(
      Object.entries(s.domain_scores || {}).map(([domain, score]) => [
        DOMAIN_SHORT_NAMES[domain] || domain,
        100 - score // Convert friction to stability score
      ])
    )
  }));

  const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#6366F1'];

  // Support both old and new field names
  const baselineStability = comparison?.baseline?.stability_index || comparison?.baseline?.mind_index || 50;
  const latestStability = comparison?.latest?.stability_index || comparison?.latest?.mind_index || 50;
  const stabilityChange = comparison?.stability_index_change || comparison?.mind_index_change || 0;
  const baselineFriction = comparison?.baseline?.neural_friction_score ?? comparison?.baseline?.cognitive_risk_index ?? 0;
  const latestFriction = comparison?.latest?.neural_friction_score ?? comparison?.latest?.cognitive_risk_index ?? 0;
  const frictionChange = latestFriction - baselineFriction;

  return (
    <div className="space-y-6" data-testid="progress-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">MindFlō Cognitive Evolution</h1>
          <p className="text-muted-foreground">Track your mental architecture improvement over time</p>
        </div>
        
        {/* Share Progress Button */}
        <Dialog>
          <DialogTrigger asChild>
            <Button onClick={fetchShareCard} data-testid="share-progress-btn">
              <Share2 className="mr-2 h-4 w-4" />
              Share My Progress
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" aria-describedby="share-dialog-desc">
            <DialogHeader>
              <DialogTitle>Share My MindFlo Progress</DialogTitle>
              <p id="share-dialog-desc" className="text-sm text-muted-foreground">Share your cognitive evolution journey</p>
            </DialogHeader>
            {shareCard && (
              <div className="space-y-4">
                {/* Share Card Preview */}
                <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center gap-2">
                      <Brain className="h-6 w-6 text-primary" strokeWidth={1.5} />
                      <span className="font-semibold">MindFlō</span>
                    </div>
                    
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">My Stability Index</p>
                      <div className="flex items-center justify-center gap-4">
                        <span className="text-3xl font-mono font-bold text-muted-foreground">
                          {shareCard.baseline_stability_index?.toFixed(0)}
                        </span>
                        <ArrowRight className="h-6 w-6 text-primary" />
                        <span className="text-3xl font-mono font-bold text-primary">
                          {shareCard.current_stability_index?.toFixed(0)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-center gap-6 text-sm">
                      <div>
                        <span className="text-emerald-500 font-bold">
                          ↑ {shareCard.stability_improvement?.toFixed(1)}
                        </span>
                        <p className="text-xs text-muted-foreground">Stability</p>
                      </div>
                      <div>
                        <span className="text-amber-500 font-bold">
                          ↓ {shareCard.neural_friction_reduction_percent}%
                        </span>
                        <p className="text-xs text-muted-foreground">Neural Friction</p>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                      Powered by MindFlō · Human Mental Operating System
                    </p>
                  </div>
                </div>
                
                {/* Share Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={copyShareText}
                  >
                    {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                    {copied ? 'Copied!' : 'Copy Text'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareCard.share_text + '\n\nPowered by MindFlō')}`, '_blank')}
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank')}
                  >
                    <Linkedin className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {comparison && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Stability Change</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-3xl font-bold font-mono ${stabilityChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {stabilityChange >= 0 ? '+' : ''}{stabilityChange.toFixed(1)}
                      </span>
                      {stabilityChange >= 0 ? (
                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  <Brain className="h-10 w-10 text-muted-foreground/30" strokeWidth={1} />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Neural Friction</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-3xl font-bold font-mono ${frictionChange <= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {frictionChange <= 0 ? '' : '+'}{frictionChange}
                      </span>
                      {frictionChange <= 0 ? (
                        <TrendingDown className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <TrendingUp className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">{baselineFriction} → {latestFriction} domains</p>
                  </div>
                  <Activity className="h-10 w-10 text-muted-foreground/30" strokeWidth={1} />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Days Tracked</p>
                    <p className="text-3xl font-bold font-mono mt-2">{comparison.days_elapsed}</p>
                  </div>
                  <Calendar className="h-10 w-10 text-muted-foreground/30" strokeWidth={1} />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Assessments</p>
                    <p className="text-3xl font-bold font-mono mt-2">{snapshots.length}</p>
                  </div>
                  <Activity className="h-10 w-10 text-muted-foreground/30" strokeWidth={1} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Stability Index Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">MindFlo Cognitive Evolution</CardTitle>
            <p className="text-sm text-muted-foreground">Tracking Stability Index, Neural Friction Score, and Belief Flexibility over time</p>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="name" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    domain={[0, 100]}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="stabilityIndex" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ fill: 'hsl(var(--primary))' }}
                    name="Stability Index"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="neuralFriction" 
                    stroke="#EF4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#EF4444' }}
                    name="Neural Friction Score"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="beliefFlexibility" 
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={{ fill: '#F59E0B' }}
                    name="Belief Flexibility"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Domain Stability Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Domain Stability Trends</CardTitle>
            <p className="text-sm text-muted-foreground">Higher scores indicate better domain stability (inverse of friction)</p>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="name" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    domain={[0, 100]}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  {Object.values(DOMAIN_SHORT_NAMES).map((name, index) => (
                    <Line 
                      key={name}
                      type="monotone" 
                      dataKey={name}
                      stroke={colors[index]}
                      strokeWidth={2}
                      dot={{ fill: colors[index], r: 3 }}
                      name={name}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Domain Improvement */}
      {comparison?.domain_changes && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Domain Improvement</CardTitle>
              <p className="text-sm text-muted-foreground">Change in neural friction scores (positive = improvement)</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(comparison.domain_changes).map(([domain, change]) => {
                  const improved = change > 0;
                  return (
                    <div 
                      key={domain}
                      className="p-4 rounded-lg bg-secondary/30 border border-border/50"
                    >
                      <p className="text-xs font-mono text-muted-foreground mb-2">
                        {DOMAIN_SHORT_NAMES[domain]}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className={`text-2xl font-bold font-mono ${improved ? 'text-emerald-500' : 'text-amber-500'}`}>
                          {improved ? '-' : '+'}{Math.abs(change).toFixed(1)}
                        </span>
                        {improved ? (
                          <TrendingDown className="h-5 w-5 text-emerald-500" />
                        ) : (
                          <TrendingUp className="h-5 w-5 text-amber-500" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {improved ? 'Friction reduced' : 'Needs attention'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Retake Assessment CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold">Continue Your Evolution</h3>
                <p className="text-sm text-muted-foreground">Retake the assessment to measure your cognitive improvement</p>
              </div>
              <Link to="/assessment/full">
                <Button className="glow-primary" data-testid="retake-assessment">
                  New Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
